/**
 * The ONE translator both clients run: nested-key lookup, key-on-miss, and
 * `{param}` interpolation over the three UI catalogs.
 *
 * It lives under `src/lib` rather than beside the catalogs in `src/i18n` for a
 * purely structural reason: `src/ios` reaches shared web modules through the
 * `@shared-lib/*` alias and the Metro watchFolder, both of which point at
 * `src/web/src/lib` only. `src/i18n/` is not on that seam (only its `messages`
 * subfolder is watched, so the JSON can cross), so a translator declared there
 * could not be imported natively and had to be hand-ported instead. The two
 * copies were character-identical, which is exactly the state that hides the
 * next divergence: change key-on-miss or the interpolation pattern here and a
 * native screen silently keeps the old behaviour.
 *
 * `src/i18n/index.ts` re-exports everything below, so every web call site keeps
 * importing from `@i18n` unchanged; `src/ios/src/lib/i18n.ts` re-exports it
 * through the native seam barrel. The catalogs themselves do NOT move - they
 * stay at `src/i18n/messages`, reached relatively from here (`@i18n` is a
 * web-only tsconfig path and is deliberately unresolvable under `src/ios`).
 */
import en from "../../i18n/messages/en.json";
import es from "../../i18n/messages/es.json";
import ptBR from "../../i18n/messages/pt-BR.json";
import { DEFAULT_UI_LOCALE, type UiLocale } from "../locale";

export type Messages = typeof en;

const catalogs: Record<UiLocale, Messages> = {
  en,
  es,
  "pt-BR": ptBR,
};

export type TranslationKey = string;

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, segment) => {
    if (current && typeof current === "object" && segment in current) {
      return (current as Record<string, unknown>)[segment];
    }
    return undefined;
  }, obj);
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params[key];
    return value !== undefined ? String(value) : `{${key}}`;
  });
}

export function getTranslator(locale: UiLocale = DEFAULT_UI_LOCALE) {
  const messages = catalogs[locale] ?? catalogs[DEFAULT_UI_LOCALE];

  function t(key: TranslationKey, params?: Record<string, string | number>): string {
    const value = getNestedValue(messages, key);
    if (typeof value !== "string") {
      return key;
    }
    return interpolate(value, params);
  }

  return { t, locale };
}

export type Translator = ReturnType<typeof getTranslator>;

/**
 * `t` returns the key itself when no message exists for it. Callers that would
 * rather show something human than a dotted key compare the result against the
 * key they passed - a rule that belongs next to the `t` that implements it,
 * not restated at each call site where it can quietly drift.
 */
export function translateOr(
  t: (key: string, params?: Record<string, string | number>) => string,
  key: string,
  fallback: string,
  params?: Record<string, string | number>,
): string {
  const label = t(key, params);
  return label === key ? fallback : label;
}
